'use client'
import { useTranslations } from 'next-intl'
import { useState } from 'react'
import { Breadcrumbs } from '@/components/breadcrumbs'
import { Modal } from '@/components/modal'
import { SlidingPane } from '@/components/sliding-pane'
import { CitiesDirectory } from '@/features/cities/cities-directory'
import { useRestaurantCitySearchContext } from '@/features/restaurants/restaurants-city-search/utils/restaurant-city-search-context'
import { SvgOutlinedChevronRight } from '@/icons/svg-outlined-chevron-right'
import { COLORS } from '@/utils/styles/colors'
import { useMobile } from '@/utils/window/use-mobile'

interface IRestaurantsCityHeaderProps {
    citySlug: string
}

/**
 * Compact left-aligned header, the sibling of the restaurant detail header.
 * The city name in the headline doubles as the city switcher: it opens the
 * cities pane (bottom sheet on mobile, side pane on desktop).
 */
export const RestaurantsCityHeader = ({ citySlug }: IRestaurantsCityHeaderProps) => {
    const { city } = useRestaurantCitySearchContext()
    const t = useTranslations()

    const [isCityPickerOpen, setIsCityPickerOpen] = useState(false)

    const isMobile = useMobile()

    // The surrounding CityPageContainer provides the page width and 32px of
    // desktop top padding, together this matches the detail page's 16/32px
    return (
        <div className="w-full flex flex-col pt-4 md:pt-0">
            <Breadcrumbs
                className="mb-2 md:mb-3"
                segments={[
                    {
                        label: t('navigation.restaurants'),
                        linkParams: { pattern: '/restaurants', params: undefined },
                    },
                ]}
            />

            <h1 className="text-display-sm">
                {t.rich('restaurants.city.restaurantsIn', {
                    cityName: city?.name ?? '',
                    city: (chunks) => (
                        <button
                            type="button"
                            onClick={() => setIsCityPickerOpen(true)}
                            className="inline-flex items-center gap-x-1 cursor-pointer select-none
                                webkit-tap-transparent group align-baseline"
                        >
                            <span className="group-hover:underline underline-offset-8">
                                {chunks}
                            </span>

                            <span className="rotate-90 flex">
                                <SvgOutlinedChevronRight color={COLORS.fg.primary} size={28} />
                            </span>
                        </button>
                    ),
                })}
            </h1>

            {/* Bottom sheet on mobile, centered modal on desktop */}
            {isMobile === false ? (
                <Modal
                    isOpen={isCityPickerOpen}
                    onClose={() => setIsCityPickerOpen(false)}
                    modalClassNameStyles="w-full max-w-[560px] m-4"
                >
                    <div className="max-h-[80vh] overflow-y-auto pb-5">
                        <CitiesDirectory
                            variant="pane"
                            selectedCitySlug={citySlug}
                            onAfterSelect={() => setIsCityPickerOpen(false)}
                        />
                    </div>
                </Modal>
            ) : (
                <SlidingPane isOpen={isCityPickerOpen} onClose={() => setIsCityPickerOpen(false)}>
                    <div className="pb-4">
                        <CitiesDirectory
                            variant="pane"
                            stickyHeaderClassName="top-14"
                            sectionScrollMarginClassName="scroll-mt-[184px]"
                            selectedCitySlug={citySlug}
                            onAfterSelect={() => setIsCityPickerOpen(false)}
                        />
                    </div>
                </SlidingPane>
            )}
        </div>
    )
}
